import { createContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { UserApi } from "@/backend/api-client";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { toast } from "sonner";

type FavoritesContextType = {
  favorites: string[];
  isFavorite: (symbol: string) => boolean;
  toggleFavorite: (symbol: string) => Promise<void>;
  isLoading: boolean;
};

export const FavoritesContext = createContext<FavoritesContextType | null>(null);

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const { userId } = useUnifiedAuth();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load once when user logs in
  useEffect(() => {
    if (!userId) {
      setFavorites([]);
      return;
    }

    setIsLoading(true);
    UserApi.getFavoriteMarkets()
      .then(setFavorites)
      .catch((err) => {
        console.error("Failed to load favorites:", err);
        setFavorites([]);
      })
      .finally(() => setIsLoading(false));
  }, [userId]);

  const isFavorite = useCallback(
    (symbol: string) => favorites.includes(symbol),
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (symbol: string) => {
      if (!userId) {
        toast.error("Please sign in to save favorites");
        return;
      }

      const newFavorites = isFavorite(symbol)
        ? favorites.filter((s) => s !== symbol)
        : [...favorites, symbol];

      // Optimistic update - change UI immediately
      setFavorites(newFavorites);

      try {
        await UserApi.updateFavoriteMarkets(newFavorites);
      } catch (error) {
        // Revert on error
        setFavorites(favorites);
        toast.error("Failed to update favorites");
        console.error("Failed to update favorites:", error);
      }
    },
    [userId, favorites, isFavorite]
  );

  return (
    <FavoritesContext.Provider value={{ favorites, isFavorite, toggleFavorite, isLoading }}>
      {children}
    </FavoritesContext.Provider>
  );
};

